import {
  getCanonicalSpellSystem,
  SPELL_SYSTEM_MANA,
} from '../spells/spellSystem';

export const RESOURCE_PROFILE_SCALAR = 'scalar';
export const RESOURCE_PROFILE_TIERED = 'tiered';

export const SPELL_BINDING_SPECIFIC = 'specific-spells';
export const SPELL_BINDING_ALL = 'all-spells';

export const SPELL_BINDING_SURFACE_SHEET = 'spell-panel';
export const SPELL_BINDING_SURFACE_COMBAT = 'combat';
export const SPELL_BINDING_SURFACE_BOTH = 'both';
export const SPELL_BINDING_SURFACE_ANY = 'any';

const NUMERIC_TRACKER_TYPES = new Set(['number', 'counter', 'progress']);

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function cloneTier(tier = {}) {
  return {
    level: Number.isFinite(tier.level) ? tier.level : 1,
    trackerId: tier.trackerId || '',
  };
}

function normalizeRecoveryEntry(entry) {
  if (entry == null || entry === false) return null;
  if (entry === 'full') return 'full';
  if (typeof entry === 'object') {
    const mode = entry.mode || 'add';
    const amount = Number.isFinite(entry.amount) ? entry.amount : 0;
    const value = Number.isFinite(entry.value) ? entry.value : 0;
    return { mode, amount, value };
  }
  return null;
}

export function createResourceProfile(kind = RESOURCE_PROFILE_SCALAR, overrides = {}) {
  return normalizeResourceProfile({
    id: makeId('profile'),
    name: 'New Resource Profile',
    visibleLabel: '',
    hiddenLabel: null,
    numericOnly: true,
    kind,
    scalar: {
      trackerId: '',
      min: 0,
      maxSource: 'tracker',
      max: null,
      allowNegative: false,
    },
    tiered: {
      tiers: [],
      spendPolicy: 'exact-level',
    },
    recovery: {
      shortRest: null,
      longRest: null,
      dayStart: null,
    },
    legacySource: null,
    ...overrides,
  });
}

export function normalizeResourceProfile(profile = {}) {
  const kind = profile.kind === RESOURCE_PROFILE_TIERED
    ? RESOURCE_PROFILE_TIERED
    : RESOURCE_PROFILE_SCALAR;

  return {
    id: profile.id || makeId('profile'),
    name: profile.name || 'Resource Profile',
    visibleLabel: profile.visibleLabel || '',
    hiddenLabel: profile.hiddenLabel ?? null,
    numericOnly: profile.numericOnly !== false,
    kind,
    scalar: {
      trackerId: profile.scalar?.trackerId || '',
      min: Number.isFinite(profile.scalar?.min) ? profile.scalar.min : 0,
      maxSource: profile.scalar?.maxSource || 'tracker',
      max: Number.isFinite(profile.scalar?.max) ? profile.scalar.max : null,
      allowNegative: profile.scalar?.allowNegative === true,
    },
    tiered: {
      tiers: toArray(profile.tiered?.tiers).map(cloneTier),
      spendPolicy: profile.tiered?.spendPolicy || 'exact-level',
    },
    recovery: {
      shortRest: normalizeRecoveryEntry(profile.recovery?.shortRest),
      longRest: normalizeRecoveryEntry(profile.recovery?.longRest),
      dayStart: normalizeRecoveryEntry(profile.recovery?.dayStart),
    },
    legacySource: profile.legacySource || null,
  };
}

export function createDirectSpellBinding(overrides = {}) {
  return normalizeDirectSpellBinding({
    id: makeId('binding'),
    spellId: null,
    resourceProfileId: null,
    assignmentMode: SPELL_BINDING_SPECIFIC,
    spellIds: [],
    castSurface: SPELL_BINDING_SURFACE_BOTH,
    costMode: 'derived',
    overrideCost: null,
    hidden: {
      existence: false,
      cost: false,
      effects: false,
    },
    ...overrides,
  });
}

export function normalizeDirectSpellBinding(binding = {}) {
  const assignmentMode = binding.assignmentMode === SPELL_BINDING_ALL
    ? SPELL_BINDING_ALL
    : SPELL_BINDING_SPECIFIC;

  const spellIds = assignmentMode === SPELL_BINDING_ALL
    ? []
    : toArray(binding.spellIds).concat(
        binding.spellId && !toArray(binding.spellIds).includes(binding.spellId)
          ? [binding.spellId]
          : []
      ).filter(Boolean);

  const castSurface = [
    SPELL_BINDING_SURFACE_SHEET,
    SPELL_BINDING_SURFACE_COMBAT,
    SPELL_BINDING_SURFACE_BOTH,
  ].includes(binding.castSurface)
    ? binding.castSurface
    : SPELL_BINDING_SURFACE_BOTH;

  const normalized = {
    id: binding.id || makeId('binding'),
    spellId: binding.spellId || spellIds[0] || null,
    resourceProfileId: binding.resourceProfileId || null,
    assignmentMode,
    spellIds,
    castSurface,
    costMode: binding.costMode || 'derived',
    overrideCost: Number.isFinite(binding.overrideCost) ? binding.overrideCost : null,
    hidden: {
      existence: binding.hidden?.existence === true,
      cost: binding.hidden?.cost === true,
      effects: binding.hidden?.effects === true,
    },
  };

  return normalized;
}

export function normalizeSystemResourceModel(system = {}) {
  return {
    ...system,
    resourceProfiles: toArray(system.resourceProfiles).map(normalizeResourceProfile),
    directSpellBindings: toArray(system.directSpellBindings).map(normalizeDirectSpellBinding),
  };
}

export function normalizeSystemsMap(systems = {}) {
  return Object.fromEntries(
    Object.entries(systems || {}).map(([id, system]) => [id, normalizeSystemResourceModel(system)])
  );
}

export function buildSystemPresetResourceModel(preset = {}, name = 'New System', trackerFields = []) {
  if (preset.type !== 'spell-resource') {
    return { resourceProfiles: [], directSpellBindings: [] };
  }

  const primaryTracker = trackerFields[0];
  const trackerIdMap = new Map(
    toArray(preset.defaultTrackerFields).map((field, index) => [
      field.id,
      trackerFields[index]?.id || field.id,
    ])
  );

  const defaultProfiles = toArray(preset.defaultResourceProfiles);
  const defaultBindings = toArray(preset.defaultDirectSpellBindings);

  if (defaultProfiles.length > 0) {
    const profileIdMap = new Map();
    const resourceProfiles = defaultProfiles.map((profile, index) => {
      const profileId = profile.id || `profile-${index + 1}`;
      profileIdMap.set(profileId, profileId);
      return normalizeResourceProfile({
        ...profile,
        id: profileId,
        scalar: {
          ...profile.scalar,
          trackerId: trackerIdMap.get(profile.scalar?.trackerId) || profile.scalar?.trackerId || '',
        },
        tiered: {
          ...profile.tiered,
          tiers: toArray(profile.tiered?.tiers).map((tier) => ({
            ...tier,
            trackerId: trackerIdMap.get(tier.trackerId) || tier.trackerId || '',
          })),
        },
      });
    });

    const directSpellBindings = defaultBindings.map((binding, index) =>
      normalizeDirectSpellBinding({
        ...binding,
        id: binding.id || `binding-${index + 1}`,
        resourceProfileId: profileIdMap.get(binding.resourceProfileId) || binding.resourceProfileId || null,
      })
    );

    return { resourceProfiles, directSpellBindings };
  }

  if (!primaryTracker) {
    return { resourceProfiles: [], directSpellBindings: [] };
  }

  const resourceProfiles = [
    createResourceProfile(RESOURCE_PROFILE_SCALAR, {
      name: `${name} Resource`,
      visibleLabel: primaryTracker.publicLabel || primaryTracker.label || 'Resource',
      scalar: {
        trackerId: primaryTracker.id,
        min: Number.isFinite(primaryTracker.min) ? primaryTracker.min : 0,
        maxSource: 'tracker',
      },
    }),
  ];

  const directSpellBindings = [
    createDirectSpellBinding({
      resourceProfileId: resourceProfiles[0].id,
      assignmentMode: SPELL_BINDING_ALL,
      castSurface: SPELL_BINDING_SURFACE_BOTH,
    }),
  ];

  return { resourceProfiles, directSpellBindings };
}

export function getBindableResourceProfiles(system = {}, options = {}) {
  const normalized = normalizeSystemResourceModel(system);
  const scalarOnly = options.scalarOnly === true;

  return normalized.resourceProfiles
    .filter((profile) => !scalarOnly || profile.kind === RESOURCE_PROFILE_SCALAR)
    .map((profile) => {
      const trackerId = profile.kind === RESOURCE_PROFILE_SCALAR
        ? profile.scalar?.trackerId || ''
        : '';
      const trackerField = trackerId ? getTrackerField(normalized, trackerId) : null;
      return {
        id: profile.id,
        kind: profile.kind,
        label: profile.visibleLabel || profile.name,
        trackerId,
        trackerLabel: trackerField?.publicLabel || trackerField?.label || '',
        trackerType: trackerField?.type || null,
      };
    });
}

export function createSystemDirectSpellBinding(system = {}, overrides = {}) {
  const defaultProfile = getBindableResourceProfiles(system, { scalarOnly: true })[0];
  return createDirectSpellBinding({
    resourceProfileId: overrides.resourceProfileId || defaultProfile?.id || null,
    ...overrides,
  });
}

export function remapSystemReferenceGraph(value, maps = {}) {
  const systemIdMap = maps.systemIdMap instanceof Map ? maps.systemIdMap : new Map(Object.entries(maps.systemIdMap || {}));
  const trackerIdMap = maps.trackerIdMap instanceof Map ? maps.trackerIdMap : new Map(Object.entries(maps.trackerIdMap || {}));
  const profileIdMap = maps.profileIdMap instanceof Map ? maps.profileIdMap : new Map(Object.entries(maps.profileIdMap || {}));

  const remapNode = (node) => {
    if (Array.isArray(node)) {
      return node.map(remapNode);
    }
    if (!node || typeof node !== 'object') {
      return node;
    }

    const next = {};
    Object.entries(node).forEach(([key, child]) => {
      if (key === 'systemId' && typeof child === 'string') {
        next[key] = systemIdMap.get(child) || child;
        return;
      }
      if (key === 'trackerId' && typeof child === 'string') {
        next[key] = trackerIdMap.get(child) || child;
        return;
      }
      if (key === 'resourceProfileId' && typeof child === 'string') {
        next[key] = profileIdMap.get(child) || child;
        return;
      }
      next[key] = remapNode(child);
    });
    return next;
  };

  return remapNode(value);
}

export function duplicateSystemResourceModel(system = {}, options = {}) {
  const normalized = normalizeSystemResourceModel(system);
  const copyToken = options.copyToken || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const nextSystemId = options.id || `${normalized.id || 'system'}-copy-${Date.now()}`;
  const nextSystemName = options.name || `${normalized.name || 'System'} (Copy)`;
  const trackerIdMap = new Map();
  const profileIdMap = new Map();

  const sections = toArray(normalized.sections).map((section) => ({
    ...section,
    id: `${section.id || 'section'}-${copyToken}`,
  }));

  const trackerFields = toArray(normalized.trackerFields).map((field) => {
    const nextId = `${field.id || 'tracker'}-${copyToken}`;
    if (field.id) trackerIdMap.set(field.id, nextId);
    return {
      ...field,
      id: nextId,
    };
  });

  const automationRules = toArray(normalized.automationRules).map((rule) => ({
    ...rule,
    id: `${rule.id || 'rule'}-${copyToken}`,
  }));

  const resourceProfiles = normalized.resourceProfiles.map((profile) => {
    const nextId = `${profile.id || 'profile'}-${copyToken}`;
    if (profile.id) profileIdMap.set(profile.id, nextId);
    return normalizeResourceProfile({
      ...profile,
      id: nextId,
      scalar: {
        ...profile.scalar,
        trackerId: trackerIdMap.get(profile.scalar?.trackerId) || profile.scalar?.trackerId || '',
      },
      tiered: {
        ...profile.tiered,
        tiers: toArray(profile.tiered?.tiers).map((tier) => ({
          ...tier,
          trackerId: trackerIdMap.get(tier.trackerId) || tier.trackerId || '',
        })),
      },
    });
  });

  const directSpellBindings = normalized.directSpellBindings.map((binding) =>
    normalizeDirectSpellBinding({
      ...binding,
      id: `${binding.id || 'binding'}-${copyToken}`,
      resourceProfileId: profileIdMap.get(binding.resourceProfileId) || binding.resourceProfileId || null,
    })
  );

  return normalizeSystemResourceModel(remapSystemReferenceGraph({
    ...system,
    id: nextSystemId,
    name: nextSystemName,
    deletable: options.deletable ?? true,
    sections,
    trackerFields,
    automationRules,
    resourceProfiles,
    directSpellBindings,
    techniques: toArray(normalized.techniques).map((technique) => ({
      ...technique,
      resourceProfileId: profileIdMap.get(technique.resourceProfileId) || technique.resourceProfileId || null,
    })),
  }, {
    systemIdMap: normalized.id ? new Map([[normalized.id, nextSystemId]]) : new Map(),
    trackerIdMap,
    profileIdMap,
  }));
}

export function adaptLegacyResourceTrackersToResourceProfiles(trackers = []) {
  return toArray(trackers)
    .filter((tracker) => Number.isFinite(tracker?.value) || Number.isFinite(tracker?.max))
    .map((tracker, index) => createResourceProfile(RESOURCE_PROFILE_SCALAR, {
      id: `legacy-resource-${tracker.id || index}`,
      name: tracker.label || `Resource ${index + 1}`,
      visibleLabel: tracker.label || `Resource ${index + 1}`,
      scalar: {
        trackerId: tracker.id || `legacy-tracker-${index}`,
        min: Number.isFinite(tracker.min) ? tracker.min : 0,
        maxSource: 'inline',
        max: Number.isFinite(tracker.max) ? tracker.max : null,
        allowNegative: false,
      },
      legacySource: {
        type: 'char.resourceTrackers',
        index,
      },
    }));
}

const CUSTOM_RESOURCE_BRIDGE_KIND = 'dm-custom-resource';

function normalizeCustomResourceTracker(tracker = {}, index = 0) {
  const fallbackId = tracker.id || `custom-resource-${index + 1}`;
  return {
    id: fallbackId,
    presetId: tracker.presetId || 'custom',
    fieldId: tracker.fieldId || null,
    label: tracker.label || `Resource ${index + 1}`,
    value: Number.isFinite(tracker.value) ? tracker.value : 0,
    max: Number.isFinite(tracker.max) ? tracker.max : 100,
    min: Number.isFinite(tracker.min) ? tracker.min : 0,
    step: Number.isFinite(tracker.step) ? tracker.step : 1,
    color: tracker.color || '#c9a227',
    type: tracker.type || 'progress',
    displayOnScreen: tracker.displayOnScreen !== false,
    showInTooltip: tracker.showInTooltip !== false,
  };
}

function getCustomResourceBridgeMeta(instance = {}) {
  return instance?.template?.resourceBridge?.kind === CUSTOM_RESOURCE_BRIDGE_KIND
    ? instance.template.resourceBridge
    : null;
}

function isManagedCustomResourceInstance(instance = {}) {
  return !!getCustomResourceBridgeMeta(instance);
}

function buildCustomResourceTemplate(tracker, existingTemplate = {}) {
  const existingField = toArray(existingTemplate.trackerFields)[0] || {};
  const trackerId = existingField.id || tracker.id;
  const existingProfile = toArray(existingTemplate.resourceProfiles)[0] || {};
  const resourceProfile = createResourceProfile(RESOURCE_PROFILE_SCALAR, {
    ...existingProfile,
    id: existingProfile.id || `profile-${tracker.id}`,
    name: tracker.label || existingProfile.name || 'Custom Resource',
    visibleLabel: tracker.label || existingProfile.visibleLabel || existingProfile.name || 'Custom Resource',
    scalar: {
      ...(existingProfile.scalar || {}),
      trackerId,
      min: tracker.min,
      maxSource: 'tracker',
      allowNegative: false,
    },
  });

  return normalizeSystemResourceModel({
    ...existingTemplate,
    id: existingTemplate.id || `custom-resource-${tracker.id}`,
    type: existingTemplate.type || 'spell-resource',
    name: tracker.label || existingTemplate.name || 'Custom Resource',
    publicName: existingTemplate.publicName || tracker.label || existingTemplate.name || 'Custom Resource',
    icon: existingTemplate.icon || '📊',
    trackerFields: [{
      ...existingField,
      id: trackerId,
      label: tracker.label || existingField.label || 'Custom Resource',
      publicLabel: existingField.publicLabel || tracker.label || existingField.label || 'Custom Resource',
      type: tracker.type || existingField.type || 'progress',
      value: tracker.value,
      min: tracker.min,
      max: tracker.max,
      step: tracker.step,
      color: tracker.color || existingField.color || '#c9a227',
      showToPlayer: tracker.displayOnScreen !== false,
      showValueToPlayer: tracker.showInTooltip !== false,
    }],
    resourceProfiles: [resourceProfile],
    directSpellBindings: toArray(existingTemplate.directSpellBindings).map(normalizeDirectSpellBinding),
    resourceBridge: {
      ...(existingTemplate.resourceBridge || {}),
      kind: CUSTOM_RESOURCE_BRIDGE_KIND,
      version: 1,
      trackerId: tracker.id,
      presetId: tracker.presetId || 'custom',
      fieldId: tracker.fieldId || null,
      displayOnScreen: tracker.displayOnScreen !== false,
      showInTooltip: tracker.showInTooltip !== false,
    },
  });
}

function buildCustomResourceInstance(tracker, existingInstance = {}) {
  const template = buildCustomResourceTemplate(tracker, existingInstance.template || {});
  const trackerField = toArray(template.trackerFields)[0] || {};
  const trackerId = trackerField.id || tracker.id;
  const currentTrackerValues = { ...(existingInstance.trackerValues || {}) };

  return {
    ...existingInstance,
    id: existingInstance.id || `custom-resource-inst-${tracker.id}`,
    systemId: existingInstance.systemId || null,
    template,
    active: existingInstance.active !== false,
    playerVisible: existingInstance.playerVisible !== false,
    showName: existingInstance.showName || false,
    showDescription: existingInstance.showDescription || false,
    showEffects: existingInstance.showEffects || false,
    showCure: existingInstance.showCure || false,
    applyEffects: existingInstance.applyEffects || false,
    applyHiddenEffects: existingInstance.applyHiddenEffects || false,
    effectOverrides: existingInstance.effectOverrides || {},
    visibleSections: existingInstance.visibleSections || {},
    showSections: existingInstance.showSections || {},
    trackerVisibility: existingInstance.trackerVisibility || {},
    trackerValues: {
      ...currentTrackerValues,
      [trackerId]: tracker.value,
    },
    instanceValues: existingInstance.instanceValues || {},
    history: existingInstance.history || [],
    appliedAt: existingInstance.appliedAt || new Date().toISOString(),
  };
}

function buildTrackersFromManagedCustomResources(systemList = []) {
  return toArray(systemList)
    .filter(isManagedCustomResourceInstance)
    .map((instance, index) => {
      const template = normalizeSystemResourceModel(instance.template || {});
      const trackerField = toArray(template.trackerFields)[0] || {};
      const bridge = getCustomResourceBridgeMeta(instance) || {};
      const trackerId = bridge.trackerId || trackerField.id || `custom-resource-${index + 1}`;
      return normalizeCustomResourceTracker({
        id: trackerId,
        presetId: bridge.presetId || 'custom',
        fieldId: bridge.fieldId || null,
        label: trackerField.publicLabel || trackerField.label || template.publicName || template.name || `Resource ${index + 1}`,
        value: instance?.trackerValues?.[trackerField.id] ?? trackerField.value ?? 0,
        max: Number.isFinite(trackerField.max) ? trackerField.max : 100,
        min: Number.isFinite(trackerField.min) ? trackerField.min : 0,
        step: Number.isFinite(trackerField.step) ? trackerField.step : 1,
        color: trackerField.color || '#c9a227',
        type: trackerField.type || 'progress',
        displayOnScreen: bridge.displayOnScreen !== false && trackerField.showToPlayer !== false,
        showInTooltip: bridge.showInTooltip !== false,
      }, index);
    });
}

function buildLegacyOnlyCustomResourceTrackers(char = {}) {
  const managedIds = new Set(
    buildTrackersFromManagedCustomResources(char.systemList || []).map((tracker) => tracker.id)
  );
  return toArray(char.resourceTrackers)
    .map((tracker, index) => normalizeCustomResourceTracker(tracker, index))
    .filter((tracker) => !managedIds.has(tracker.id));
}

export function getCharacterCustomResourceTrackers(char = {}) {
  const managed = buildTrackersFromManagedCustomResources(char.systemList || []);
  const legacy = buildLegacyOnlyCustomResourceTrackers(char);

  if (managed.length === 0) {
    return toArray(char.resourceTrackers).map((tracker, index) =>
      normalizeCustomResourceTracker(tracker, index)
    );
  }

  return [...managed, ...legacy];
}

export function getLegacyOnlyCharacterCustomResourceTrackers(char = {}) {
  return buildLegacyOnlyCustomResourceTrackers(char);
}

function dedupeCustomResourceTrackers(trackers = []) {
  const deduped = [];
  const indexById = new Map();
  toArray(trackers).forEach((tracker, index) => {
    const normalized = normalizeCustomResourceTracker(tracker, index);
    const existingIndex = indexById.get(normalized.id);
    if (existingIndex == null) {
      indexById.set(normalized.id, deduped.length);
      deduped.push(normalized);
      return;
    }
    deduped[existingIndex] = normalized;
  });
  return deduped;
}

export function syncCharacterCustomResources(char = {}, nextTrackers = null) {
  const sourceTrackers = dedupeCustomResourceTrackers(nextTrackers ?? getCharacterCustomResourceTrackers(char));
  const nextChar = cloneChar(char);
  const existingManaged = toArray(nextChar.systemList).filter(isManagedCustomResourceInstance);
  const existingByTrackerId = new Map();

  existingManaged.forEach((instance, index) => {
    const bridge = getCustomResourceBridgeMeta(instance);
    const trackerId = bridge?.trackerId || toArray(instance?.template?.trackerFields)[0]?.id || `custom-resource-${index + 1}`;
    existingByTrackerId.set(trackerId, instance);
  });

  const managedInstances = sourceTrackers.map((tracker) =>
    buildCustomResourceInstance(tracker, existingByTrackerId.get(tracker.id))
  );

  nextChar.systemList = [
    ...toArray(nextChar.systemList).filter((instance) => !isManagedCustomResourceInstance(instance)),
    ...managedInstances,
  ];
  nextChar.resourceTrackers = [
    ...buildTrackersFromManagedCustomResources(nextChar.systemList),
    ...buildLegacyOnlyCustomResourceTrackers(nextChar),
  ];
  return nextChar;
}

function bindingMatchesSurface(binding, surface) {
  return binding.castSurface === SPELL_BINDING_SURFACE_BOTH || binding.castSurface === surface;
}

export function resolveDirectSpellBinding(system, spellId, surface = SPELL_BINDING_SURFACE_SHEET) {
  if (!spellId) return null;
  const normalized = normalizeSystemResourceModel(system);
  const bindings = normalized.directSpellBindings.filter((binding) => bindingMatchesSurface(binding, surface));
  const specific = bindings.find((binding) =>
    binding.assignmentMode === SPELL_BINDING_SPECIFIC && binding.spellIds.includes(spellId)
  );
  if (specific) return specific;
  return bindings.find((binding) => binding.assignmentMode === SPELL_BINDING_ALL) || null;
}

function getSystemTemplate(systemId, systems, fallbackTemplate = null) {
  const system = systems?.[systemId] || fallbackTemplate;
  return system ? normalizeSystemResourceModel(system) : null;
}

function getAppliedInstance(char, binding) {
  const list = toArray(char?.systemList);
  if (binding?.instanceId) {
    const exact = list.find((instance) => instance.id === binding.instanceId && instance.active !== false);
    if (exact) return exact;
  }
  return list.find((instance) => instance.systemId === binding?.systemId && instance.active !== false) || null;
}

function getResourceProfile(system, resourceProfileId) {
  return toArray(system?.resourceProfiles).find((profile) => profile.id === resourceProfileId) || null;
}

function getTrackerField(system, trackerId) {
  return toArray(system?.trackerFields).find((field) => field.id === trackerId) || null;
}

function buildProfileBinding({
  systemId,
  instanceId,
  profile,
  binding = null,
  sourceType,
}) {
  return {
    sourceType,
    kind: profile.kind === RESOURCE_PROFILE_TIERED ? 'system-tiered' : 'system-scalar',
    systemId,
    instanceId,
    resourceProfileId: profile.id,
    profile,
    binding,
    costMode: binding?.costMode || 'derived',
    overrideCost: binding?.overrideCost ?? null,
  };
}

export function getSpellResourceBinding({
  char,
  spell,
  systems = {},
  surface = SPELL_BINDING_SURFACE_SHEET,
  technique = null,
  preferredSystemId = null,
} = {}) {
  if (technique?.resourceProfileId) {
    const systemId = technique.systemId || preferredSystemId;
    const system = getSystemTemplate(systemId, normalizeSystemsMap(systems));
    const profile = getResourceProfile(system, technique.resourceProfileId);
    if (profile) {
      return buildProfileBinding({
        systemId,
        instanceId: technique.instanceId || null,
        profile,
        binding: null,
        sourceType: 'technique',
      });
    }
  }

  if (technique) {
    return {
      sourceType: 'technique',
      kind: 'technique-explicit',
      systemId: technique.systemId || preferredSystemId || null,
      instanceId: technique.instanceId || null,
      resourceProfileId: technique.resourceProfileId || null,
      techniqueId: technique.id || null,
    };
  }

  const normalizedSystems = normalizeSystemsMap(systems);
  const activeInstances = toArray(char?.systemList).filter((instance) => instance.active !== false);
  let fallbackBinding = null;

  activeInstances.forEach((instance) => {
    if (!spell?.id) return;
    const system = getSystemTemplate(instance.systemId, normalizedSystems, instance.template);
    if (!system) return;
    const binding = resolveDirectSpellBinding(system, spell.id, surface);
    if (!binding) return;
    const profile = getResourceProfile(system, binding.resourceProfileId);
    if (!profile) return;
    const resolved = buildProfileBinding({
      systemId: instance.systemId,
      instanceId: instance.id || null,
      profile,
      binding,
      sourceType: 'direct-spell',
    });
    if (binding.assignmentMode === SPELL_BINDING_SPECIFIC) {
      fallbackBinding = resolved;
      return;
    }
    if (!fallbackBinding) {
      fallbackBinding = resolved;
    }
  });

  if (fallbackBinding) {
    return fallbackBinding;
  }

  const spellSystem = getCanonicalSpellSystem(char?.settings || {});
  return {
    sourceType: 'legacy',
    kind: spellSystem === SPELL_SYSTEM_MANA ? 'legacy-mana' : 'legacy-slots',
    systemId: null,
    instanceId: null,
    resourceProfileId: null,
  };
}

function buildUnavailableState(reason, extra = {}) {
  return {
    available: false,
    reason,
    ...extra,
  };
}

function getTierState(system, instance, tier = {}) {
  const tracker = getTrackerField(system, tier.trackerId);
  const current = instance?.trackerValues?.[tier.trackerId] ?? tracker?.value ?? 0;
  const min = Number.isFinite(tracker?.min) ? tracker.min : 0;
  const max = Number.isFinite(tracker?.max) ? tracker.max : null;
  return {
    level: Number.isFinite(tier.level) ? tier.level : 1,
    trackerId: tier.trackerId || '',
    current,
    min,
    max,
    label: tracker?.publicLabel || tracker?.label || `Level ${tier.level || 1}`,
  };
}

function resolveTierLevel(options = {}) {
  if (Number.isFinite(options.castLevel)) return options.castLevel;
  if (Number.isFinite(options.level)) return options.level;
  if (Number.isFinite(options.tierLevel)) return options.tierLevel;
  if (Number.isFinite(options.slotLevel)) return options.slotLevel;
  return null;
}

export function getBoundResourceState(char, systems, binding) {
  if (!binding) {
    return buildUnavailableState('missing-binding');
  }

  if (binding.kind === 'legacy-mana') {
    return {
      available: true,
      kind: 'legacy-mana',
      current: char?.currentMana ?? 0,
      min: 0,
      max: Number.isFinite(char?.maxMana) ? char.maxMana : null,
      label: 'Mana',
    };
  }

  if (binding.kind === 'legacy-slots') {
    return {
      available: true,
      kind: 'legacy-slots',
      tiers: {
        spellSlots: char?.spellSlots || {},
        usedSpellSlots: char?.usedSpellSlots || {},
      },
      label: 'Spell Slots',
    };
  }

  if (binding.kind === 'legacy-tracker-scalar') {
    const index = binding.legacyTrackerIndex;
    const tracker = index == null ? null : toArray(char?.resourceTrackers)[index];
    if (!tracker) {
      return buildUnavailableState('missing-legacy-tracker', { binding });
    }
    return {
      available: true,
      kind: 'legacy-tracker-scalar',
      current: tracker.value ?? 0,
      min: Number.isFinite(tracker.min) ? tracker.min : 0,
      max: Number.isFinite(tracker.max) ? tracker.max : null,
      trackerId: tracker.id || binding.resourceProfileId,
      label: tracker.label || 'Resource',
      binding,
    };
  }

  if (binding.kind !== 'system-scalar' && binding.kind !== 'system-tiered') {
    return buildUnavailableState('unsupported-binding-kind', { binding });
  }

  const instance = getAppliedInstance(char, binding);
  if (!instance) {
    return buildUnavailableState('missing-system-instance', { binding });
  }

  const system = getSystemTemplate(binding.systemId, normalizeSystemsMap(systems), instance.template);
  if (!system) {
    return buildUnavailableState('missing-system-template', { binding });
  }

  const profile = getResourceProfile(system, binding.resourceProfileId);
  if (!profile) {
    return buildUnavailableState('missing-resource-profile', { binding });
  }

  if (profile.kind === RESOURCE_PROFILE_TIERED) {
    return {
      available: true,
      kind: 'system-tiered',
      tiers: toArray(profile.tiered?.tiers)
        .map((tier) => getTierState(system, instance, tier))
        .sort((left, right) => left.level - right.level),
      label: profile.visibleLabel || profile.name,
      binding,
      profile,
      system,
      instance,
    };
  }

  const trackerId = profile.scalar?.trackerId;
  const trackerField = getTrackerField(system, trackerId);
  if (!trackerField) {
    return buildUnavailableState('missing-tracker-field', { binding, trackerId });
  }
  if (!NUMERIC_TRACKER_TYPES.has(trackerField.type || '')) {
    return buildUnavailableState('non-numeric-tracker', { binding, trackerId });
  }

  const current = instance.trackerValues?.[trackerId] ?? trackerField.value ?? 0;
  const min = Number.isFinite(profile.scalar?.min)
    ? profile.scalar.min
    : (Number.isFinite(trackerField.min) ? trackerField.min : 0);
  const max = profile.scalar?.maxSource === 'inline'
    ? profile.scalar.max
    : (Number.isFinite(trackerField.max) ? trackerField.max : profile.scalar.max);

  return {
    available: true,
    kind: 'system-scalar',
    current,
    min,
    max,
    trackerId,
    label: profile.visibleLabel || trackerField.publicLabel || trackerField.label || profile.name,
    binding,
    profile,
    system,
    instance,
  };
}

export function getDisplayableSystemResourceRows(char, systems = {}) {
  const normalizedSystems = normalizeSystemsMap(systems);
  const activeInstances = toArray(char?.systemList).filter((instance) => instance.active !== false);
  const rows = [];

  activeInstances.forEach((instance) => {
    const system = getSystemTemplate(instance.systemId, normalizedSystems, instance.template);
    if (!system) return;

    const scalarProfiles = getBindableResourceProfiles(system, { scalarOnly: true });
    scalarProfiles.forEach((profileOption) => {
      const profile = getResourceProfile(system, profileOption.id);
      if (!profile) return;

      const trackerField = getTrackerField(system, profile.scalar?.trackerId);
      const trackerVis = instance.trackerVisibility?.[profile.scalar?.trackerId] || {};
      const bridge = getCustomResourceBridgeMeta(instance);
      const isManagedCustomResource = bridge?.kind === CUSTOM_RESOURCE_BRIDGE_KIND && bridge?.trackerId === profile.scalar?.trackerId;
      const isDirectSpellProfile = toArray(system.directSpellBindings).some(
        (binding) => binding.resourceProfileId === profile.id
      );
      const showToPlayer = trackerVis.showToPlayer !== false && trackerField?.showToPlayer !== false;
      const showOnBar = trackerVis.showBar !== false;
      const displayAllowed = isManagedCustomResource
        ? bridge?.displayOnScreen !== false
        : isDirectSpellProfile;
      if (!trackerField || !showToPlayer || !showOnBar || !displayAllowed) {
        return;
      }

      const state = getBoundResourceState(char, normalizedSystems, buildProfileBinding({
        systemId: instance.systemId,
        instanceId: instance.id || null,
        profile,
        binding: null,
        sourceType: 'system-display',
      }));

      if (!state.available || state.kind !== 'system-scalar') {
        return;
      }

      rows.push({
        id: `${instance.id || instance.systemId}:${profile.id}`,
        sourceType: isManagedCustomResource ? 'dm-custom-resource' : 'system-scalar',
        systemId: instance.systemId,
        instanceId: instance.id || null,
        resourceProfileId: profile.id,
        label: state.label,
        current: state.current,
        min: state.min,
        max: state.max,
        color: trackerField.color || '#c9a227',
        icon: system.icon || '🔮',
        trackerType: trackerField.type || 'progress',
        systemName: system.publicName || system.name || instance.systemId,
      });
    });
  });

  return rows;
}

export function getDirectSpellResourceBindings(char, systems = {}, surface = SPELL_BINDING_SURFACE_SHEET) {
  if (surface === SPELL_BINDING_SURFACE_ANY) {
    const seen = new Set();
    return [
      ...getDirectSpellResourceBindings(char, systems, SPELL_BINDING_SURFACE_SHEET),
      ...getDirectSpellResourceBindings(char, systems, SPELL_BINDING_SURFACE_COMBAT),
    ].filter((binding) => {
      const key = `${binding.instanceId || binding.systemId}:${binding.resourceProfileId}:${binding.binding?.id || 'binding'}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  const normalizedSystems = normalizeSystemsMap(systems);
  const activeInstances = toArray(char?.systemList).filter((instance) => instance.active !== false);
  const seen = new Set();
  const bindings = [];

  activeInstances.forEach((instance) => {
    const system = getSystemTemplate(instance.systemId, normalizedSystems, instance.template);
    if (!system) return;

    toArray(system.directSpellBindings)
      .filter((binding) => bindingMatchesSurface(binding, surface))
      .forEach((binding) => {
        const profile = getResourceProfile(system, binding.resourceProfileId);
        if (!profile) return;

        const key = `${instance.id || instance.systemId}:${profile.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        bindings.push(buildProfileBinding({
          systemId: instance.systemId,
          instanceId: instance.id || null,
          profile,
          binding,
          sourceType: 'direct-spell',
        }));
      });
  });

  return bindings;
}

export function quoteBoundResourceSpend(char, systems, binding, options = {}) {
  const state = getBoundResourceState(char, systems, binding);
  if (!state.available) {
    return { ok: false, reason: state.reason, state, binding };
  }

  if (binding.kind === 'legacy-slots') {
    return { ok: false, reason: 'tiered-not-implemented', state, binding };
  }

  if (state.kind === 'system-tiered') {
    const tierLevel = resolveTierLevel(options);
    if (!Number.isFinite(tierLevel)) {
      return { ok: false, reason: 'missing-tier-level', state, binding };
    }

    const tier = state.tiers.find((entry) => entry.level === tierLevel);
    if (!tier) {
      return { ok: false, reason: 'missing-tier', state, binding, tierLevel };
    }

    const amount = binding?.costMode === 'free'
      ? 0
      : (Number.isFinite(options.amount) ? options.amount : 1);
    const next = tier.current - amount;
    if (next < (tier.min ?? 0)) {
      return { ok: false, reason: 'insufficient-resource', state, binding, amount, tierLevel, tier, next };
    }

    return {
      ok: true,
      amount,
      next,
      state,
      binding,
      tier,
      tierLevel,
    };
  }

  const amount = binding?.costMode === 'free'
    ? 0
    : Number.isFinite(options.amount)
      ? options.amount
      : (binding?.costMode === 'override' && Number.isFinite(binding.overrideCost)
          ? binding.overrideCost
          : (Number.isFinite(options.derivedCost) ? options.derivedCost : null));

  if (!Number.isFinite(amount)) {
    return { ok: false, reason: 'missing-cost', state, binding };
  }

  const next = state.current - amount;
  if (next < (state.min ?? 0)) {
    return { ok: false, reason: 'insufficient-resource', state, binding, amount, next };
  }

  return {
    ok: true,
    amount,
    next,
    state,
    binding,
  };
}

function cloneChar(char) {
  return {
    ...(char || {}),
    systemList: toArray(char?.systemList).map((instance) => ({
      ...instance,
      trackerValues: { ...(instance?.trackerValues || {}) },
    })),
    resourceTrackers: toArray(char?.resourceTrackers).map((tracker) => ({ ...tracker })),
  };
}

export function applyBoundResourceSpend(char, systems, binding, quote) {
  if (!quote?.ok) {
    return { nextChar: char, resourceEvents: [] };
  }

  const nextChar = cloneChar(char);
  const amount = quote.amount;

  if (binding.kind === 'legacy-mana') {
    nextChar.currentMana = quote.next;
  } else if (binding.kind === 'legacy-tracker-scalar') {
    const index = binding.legacyTrackerIndex;
    if (index != null && nextChar.resourceTrackers[index]) {
      nextChar.resourceTrackers[index].value = quote.next;
    }
  } else if (binding.kind === 'system-scalar') {
    const target = nextChar.systemList.find((instance) =>
      (binding.instanceId ? instance.id === binding.instanceId : instance.systemId === binding.systemId)
    );
    if (target) {
      target.trackerValues = {
        ...(target.trackerValues || {}),
        [quote.state.trackerId]: quote.next,
      };
    }
  } else if (binding.kind === 'system-tiered') {
    const target = nextChar.systemList.find((instance) =>
      (binding.instanceId ? instance.id === binding.instanceId : instance.systemId === binding.systemId)
    );
    if (target && quote.tier?.trackerId) {
      target.trackerValues = {
        ...(target.trackerValues || {}),
        [quote.tier.trackerId]: quote.next,
      };
    }
  }

  return {
    nextChar,
    resourceEvents: [{
      kind: 'spend',
      amount,
      resource: binding.kind,
      resourceProfileId: binding.resourceProfileId || null,
      systemId: binding.systemId || null,
      trackerId: quote.tier?.trackerId || quote.state?.trackerId || null,
      level: quote.tierLevel ?? quote.tier?.level ?? null,
    }],
  };
}

function applyRecoveryValue(current, max, recovery) {
  if (recovery == null || recovery === false) return current;
  if (recovery === 'full') return Number.isFinite(max) ? max : current;
  if (recovery.mode === 'set') return recovery.value;
  if (recovery.mode === 'add') {
    const next = current + recovery.amount;
    return Number.isFinite(max) ? Math.min(max, next) : next;
  }
  return current;
}

export function recoverBoundResource(char, systems, binding, restType) {
  const state = getBoundResourceState(char, systems, binding);
  if (!state.available || (state.kind !== 'system-scalar' && state.kind !== 'system-tiered' && state.kind !== 'legacy-mana' && state.kind !== 'legacy-tracker-scalar')) {
    return { nextChar: char, resourceEvents: [] };
  }

  const recoveryPolicy = state.profile?.recovery?.[restType] ?? null;
  if (recoveryPolicy == null) {
    return { nextChar: char, resourceEvents: [] };
  }

  if (state.kind === 'system-tiered') {
    const nextChar = cloneChar(char);
    const target = nextChar.systemList.find((instance) =>
      (binding.instanceId ? instance.id === binding.instanceId : instance.systemId === binding.systemId)
    );
    if (!target) {
      return { nextChar: char, resourceEvents: [] };
    }

    const resourceEvents = [];
    state.tiers.forEach((tier) => {
      const nextValue = applyRecoveryValue(tier.current, tier.max, recoveryPolicy);
      const delta = nextValue - tier.current;
      if (delta === 0 || !tier.trackerId) {
        return;
      }

      target.trackerValues = {
        ...(target.trackerValues || {}),
        [tier.trackerId]: nextValue,
      };
      resourceEvents.push({
        kind: 'gain',
        amount: delta,
        resource: binding.kind,
        resourceProfileId: binding.resourceProfileId || null,
        systemId: binding.systemId || null,
        trackerId: tier.trackerId,
        level: tier.level,
      });
    });

    if (resourceEvents.length === 0) {
      return { nextChar: char, resourceEvents: [] };
    }

    return { nextChar, resourceEvents };
  }

  const nextValue = applyRecoveryValue(state.current, state.max, recoveryPolicy);
  const delta = nextValue - state.current;
  if (delta === 0) {
    return { nextChar: char, resourceEvents: [] };
  }

  const nextChar = cloneChar(char);
  if (binding.kind === 'legacy-mana') {
    nextChar.currentMana = nextValue;
  } else if (binding.kind === 'legacy-tracker-scalar') {
    const index = binding.legacyTrackerIndex;
    if (index != null && nextChar.resourceTrackers[index]) {
      nextChar.resourceTrackers[index].value = nextValue;
    }
  } else if (binding.kind === 'system-scalar') {
    const target = nextChar.systemList.find((instance) =>
      (binding.instanceId ? instance.id === binding.instanceId : instance.systemId === binding.systemId)
    );
    if (target) {
      target.trackerValues = {
        ...(target.trackerValues || {}),
        [state.trackerId]: nextValue,
      };
    }
  }

  return {
    nextChar,
    resourceEvents: [{
      kind: 'gain',
      amount: delta,
      resource: binding.kind,
      resourceProfileId: binding.resourceProfileId || null,
      systemId: binding.systemId || null,
      trackerId: state.trackerId || null,
    }],
  };
}

export function recoverDirectSpellResources(char, systems, restType, surface = SPELL_BINDING_SURFACE_ANY) {
  const bindings = getDirectSpellResourceBindings(char, systems, surface);
  if (bindings.length === 0) {
    return { nextChar: char, resourceEvents: [], bindings: [] };
  }

  let nextChar = char;
  const resourceEvents = [];
  bindings.forEach((binding) => {
    const result = recoverBoundResource(nextChar, systems, binding, restType);
    nextChar = result.nextChar;
    if (result.resourceEvents?.length) {
      resourceEvents.push(...result.resourceEvents);
    }
  });

  return { nextChar, resourceEvents, bindings };
}
